import { Divider, Grid, Stack, Text } from '@mantine/core';
import { formatCurrency } from 'src/common/currency';
import type { MatchSuggestion } from '../../types';

type MatchComparisonProps = {
  suggestion: MatchSuggestion;
};

function humaniseEnum(value: string): string {
  const lower = value.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function ComparisonRow({
  label,
  left,
  right,
  last,
}: {
  label: string;
  left: string;
  right: string;
  last?: boolean;
}) {
  return (
    <>
      <Grid gap="md" align="center">
        <Grid.Col span={3}>
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Grid.Col>
        <Grid.Col span={4}>
          <Text size="xs">{left}</Text>
        </Grid.Col>
        <Grid.Col span={5}>
          <Text size="xs">{right}</Text>
        </Grid.Col>
      </Grid>
      {!last && <Divider color="gray.2" />}
    </>
  );
}

function ContextRow({ label, value }: { label: string; value: string }) {
  if (value === '—') return null;

  return (
    <Grid gap="md" align="center">
      <Grid.Col span={3}>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
      </Grid.Col>
      <Grid.Col span={9}>
        <Text size="xs">{value}</Text>
      </Grid.Col>
    </Grid>
  );
}

export default function MatchComparison({ suggestion }: MatchComparisonProps) {
  const { unprocessedProduct: feed, suggestedAsset: asset, matchReasons } = suggestion;

  return (
    <Stack gap="sm">
      <Grid gap="md">
        <Grid.Col span={3} />
        <Grid.Col span={4}>
          <Text size="xs" fw={600} c="dimmed">
            Feed
          </Text>
        </Grid.Col>
        <Grid.Col span={5}>
          <Text size="xs" fw={600} c="dimmed">
            On System
          </Text>
        </Grid.Col>
      </Grid>
      <Divider color="gray.2" />

      <ComparisonRow
        label="Reference"
        left={feed.providerReferenceNumber}
        right={asset.providerReferenceNumber}
      />
      <ComparisonRow label="Product" left={feed.productName ?? '—'} right={asset.description} />
      <ComparisonRow
        label="Value"
        left={
          feed.value != null
            ? (formatCurrency(feed.value, 'GBP', { fractionDigits: 0 }) ?? '—')
            : '—'
        }
        right={
          asset.value != null
            ? (formatCurrency(asset.value, 'GBP', { fractionDigits: 0 }) ?? '—')
            : '—'
        }
      />
      <ComparisonRow
        label="Type"
        left={feed.productType ? humaniseEnum(feed.productType) : '—'}
        right={humaniseEnum(asset.product)}
        last
      />

      {(feed.schemeName || feed.valueDate) && (
        <>
          <Divider />
          <Text size="xs" fw={600} c="dimmed">
            Feed only
          </Text>
          <ContextRow label="Scheme" value={feed.schemeName ?? '—'} />
          <ContextRow label="Value Date" value={feed.valueDate ?? '—'} />
        </>
      )}

      <Divider />
      <Stack gap={2}>
        {matchReasons.map((reason, i) => (
          <Text key={i} size="xs" c="dimmed" fs="italic">
            {reason.detail}
          </Text>
        ))}
      </Stack>
    </Stack>
  );
}
